import React, { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { LinearProgress } from '@mui/material';
import {  Delete as DeleteIcon, 
          PersonAdd as PersonIcon, 
          Power as PowerIcon, 
          ReportProblem as ReportProblemIcon 
        } from '@mui/icons-material';
import { useTranslation } from 'react-i18next'; 
import { convertDateToText } from '../utils';
import {  deleteBooking, 
          fetchBookingsByDate, 
          bookSlotOther, 
          fetchCompanies, 
          reportProblem,
          deleteReport 
        } from '../api/bookings';
import { fetchUsers } from '../api/users';
import { fetchConfigurations } from '../api/configurations';
import { getOccupancyColor } from '../utils';
import ConfirmDialog from '../components/ConfirmDialog';
import ReportDialog from '../components/ReportDialog';


const BookingList = ({ selectedDate, setSnackbar, reloadCalendarData }) => {
  const selectedDay = dayjs(selectedDate).startOf('day');
  const now = dayjs().startOf('day');
  const isToday = selectedDay.isSame(now, 'day');
  const isTodayOrLater = isToday || selectedDay.isAfter(now, 'day');
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [occupancyPercent, setOccupancyPercent] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, bookingId: null });
  const formattedDate = selectedDate ? convertDateToText(selectedDate, t) : t('actions.select_date');
  const [newBooking, setNewBooking] = useState({ isGuest: true, guestName: '', guestCompany: '', userId: '' });
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [thresholds, setThresholds] = useState({ high: 75, medium: 50 });
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [reportIdToRemove, setReportIdToRemove] = useState(null);
  const [reportContent, setReportContent] = useState(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportDialog, setReportDialog] = useState({
    open: false,
    bookingId: null,
    plateNumber: '',
    comments: ''
  });

  const openConfirmDialog = (bookingId) => setConfirmDialog({ open: true, bookingId });
  const closeConfirmDialog = () => setConfirmDialog({ open: false, bookingId: null });
  const confirmDeleteBooking = async () => {
    if (confirmDialog.bookingId) {
      await handleDeleteBooking(confirmDialog.bookingId);
      closeConfirmDialog();
    }
  };

  const openReportDialog = (bookingId) =>
    setReportDialog({ open: true, bookingId, plateNumber: '', comments: '' });
  const closeReportDialog = () =>
    setReportDialog({ open: false, bookingId: null, plateNumber: '', comments: '' });

  const viewReportDetails = (report) => {
    setReportIdToRemove(report.id);
    setReportContent(
      <>
        <Typography><strong>Number Plate:</strong> {report.number_plate}</Typography>
        <Typography><strong>Comments:</strong> {report.comments}</Typography>
      </>
    );
    setIsReportOpen(true);
  };

  const removeReport = async (reportId) => {
    try {
      await deleteReport(reportId);
      setSnackbar({ open: true, message: 'Report removed successfully.', severity: 'success' });
      loadBookings();
      setIsReportOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove report.', severity: 'error' });
    }
  };
  
  const handleReportProblem = async () => {
    const { bookingId, plateNumber, comments } = reportDialog;
    try {
      await reportProblem(bookingId, plateNumber, comments);
      setSnackbar({ open: true, message: 'Problem reported successfully.', severity: 'success' });
      loadBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      closeReportDialog();
    }
  };

  const loadCompanies = useCallback(async () => {
    try {
        const data = await fetchCompanies();
        setCompanySuggestions(data.companies || []);
    } catch (err) {
        console.error('Error fetching companies:', err);
    }
  }, []);

  useEffect(() => {
      loadCompanies();
  }, [loadCompanies]);


  const loadThresholds = useCallback(async () => {
    try {
      const data = await fetchConfigurations();
      setThresholds({
        high: parseFloat(data.HIGH_OCCUPANCY || 75),
        medium: parseFloat(data.MEDIUM_OCCUPANCY || 50),
      });
    } catch (err) {
      console.error('Error fetching configuration thresholds:', err);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const data = await fetchBookingsByDate(selectedDate);
      setBookings(data.bookings || []);
      setOccupancyPercent(data.occupancy_percent || 0);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  }, [selectedDate]);

  const toggleUsers = useCallback(
    (isGuest) => {
      if (isGuest) {
        setUsers(allUsers);
      } else {
        const nonGuestBookings = bookings.filter((booking) => !booking.is_guest);
        const bookedUserIds = nonGuestBookings.map((booking) => booking.user_id);
        const availableUsers = allUsers.filter((user) => !bookedUserIds.includes(user.id));
        setUsers(availableUsers);
      }
    },
    [allUsers, bookings]
  );
  
  const loadUsers = useCallback(async () => {
    try {
      const data = await fetchUsers();
      setAllUsers(data.users); // Load all users once
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, bookingId: null });
  };

  const handleDeleteBooking = async () => {    
    if (!confirmDialog.bookingId) return;
    try {
      await deleteBooking(confirmDialog.bookingId);
      setSnackbar({ open: true, message: t('bookings.deleted_success'), severity: 'success' });
      handleCloseConfirmDialog();
      loadBookings();
      reloadCalendarData();
    } catch (err) {
      console.error('Error deleting booking:', err);
      setSnackbar({ open: true, message: err.message || t('booking.cancelation_problem'), severity: 'error' });
    }
  };

  const handleBookingSubmit = async () => {
    try {
      const bookingData = {
        date: selectedDate,
        userId: newBooking.userId,
      };
  
      if (newBooking.isGuest) {
        bookingData.guestName = newBooking.guestName;
        bookingData.guestCompany = newBooking.guestCompany;
      }
  
      await bookSlotOther(bookingData);
  
      setSnackbar({ open: true, message: t('bookings.created_success'), severity: 'success' });
  
      setNewBooking({ isGuest: true, guestName: '', guestCompany: '', userId: '' });
      setUsers(allUsers); 
  
      loadBookings();
      reloadCalendarData();
    } catch (err) {
      console.error('Error creating booking:', err);
      setSnackbar({ open: true, message: err.message || t('booking.created.error'), severity: 'error' });
    }
  };
  

  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]); 
  
  useEffect(() => {
    loadBookings();
  }, [loadBookings]); 
  
  useEffect(() => {
    toggleUsers(newBooking.isGuest);
  }, [newBooking.isGuest, toggleUsers]);
  
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const isSynthetic = (booking) => typeof booking.id === 'string';

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="h6">
        {t('bookings.for')} {formattedDate}
      </Typography>

      <Box sx={{ mt: 2, mb: 4 }}>
        <LinearProgress
          variant="determinate"
          value={occupancyPercent}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: 'grey.300',
            '& .MuiLinearProgress-bar': {
              backgroundColor: getOccupancyColor(occupancyPercent, thresholds),
            },
          }}
        />
      </Box>

      {!selectedDate || bookings.length === 0 ? (
        <Typography>{t('bookings.no_bookings')}</Typography>
      ) : (
        <List>
          {bookings.map((booking) => (
            <React.Fragment key={booking.id}>
            <ListItem
              sx={{
                backgroundColor: isSynthetic(booking) ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
                borderRadius: 1
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>

                  {booking.report ? (
                    <Tooltip title={t('report.view_report')}>
                      <IconButton
                        onClick={() => viewReportDetails(booking.report)}
                        sx={{ color: 'error.main' }}
                        aria-label="View Report"
                      >
                        <ReportProblemIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    isToday && (
                      <Tooltip title={t('bookings.report_problem')}>
                        <IconButton onClick={() => openReportDialog(booking.id)} aria-label="Report Problem">
                          <ReportProblemIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  )}

                  {!isSynthetic(booking) && !booking.report && isTodayOrLater && (
                    <Tooltip title={t('actions.cancel')}>
                      <IconButton
                        onClick={() => openConfirmDialog(booking.id)}
                        sx={{ color: 'error.main' }}
                        aria-label="Delete Booking"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>
                      {t('slots.slot')}: {booking.slot_number}
                    </span>
                    {booking.slot_has_charger && <PowerIcon fontSize="small" sx={{ color: 'primary.main' }} />}
                    <span>, {t('general.address')}: {booking.address}</span>
                  </Box>
                }
            secondary={
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>
                    {t('general.email')}: {booking.email}
                  </span>
                </Box>
                {booking.is_guest && (
                  <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
                    <Tooltip title={t('user.guest')}>
                      <PersonIcon fontSize="small" color="primary" />
                    </Tooltip>
                    <span>
                      {t('user.guest')}: {booking.guestName}
                    </span>
                    <span>
                      {t('user.guest_company')}: {booking.guestCompany}
                    </span>
                  </Box>
                )}
                {booking.report && (
                  <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
                    <span>
                      {t('report.number_plate')}: {booking.report.number_plate}
                    </span>
                    <span>
                      {t('report.comments')}: {booking.report.comments}
                    </span>
                  </Box>
                )}
              </Box>
            }
          />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDeleteBooking}
        title={t('actions.confirm_cancelation')}
        children={t('bookings.confirm_deletion_question')}
        t={t}
      />
      <ReportDialog
        open={reportDialog.open}
        plateNumber={reportDialog.plateNumber}
        comments={reportDialog.comments}
        onClose={closeReportDialog}
        onSubmit={handleReportProblem}
        setReportDialog={setReportDialog}
        t={t}
      />
      <ConfirmDialog
        open={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        onConfirm={() => removeReport(reportIdToRemove)}
        title={t('bookings.report_info')}
        children={reportContent}
        t={t}
      />
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          {t('bookings.new_booking')}
        </Typography>

        <Box mb={2}>
        <FormControlLabel
            control={
              <Switch
                checked={newBooking.isGuest}
                onChange={() => {
                  const newIsGuest = !newBooking.isGuest;
                  setNewBooking((prev) => ({ ...prev, isGuest: newIsGuest }));
                  toggleUsers(newIsGuest); 
                }}
                color="primary"
              />
            }
            label={newBooking.isGuest ? t('user.guest') : t('user.employee')}
          />
        </Box>

        {newBooking.isGuest ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              options={users}
              getOptionLabel={(user) => user.email || ''} 
              value={users.find((user) => user.id === newBooking.userId) || null} 
              renderInput={(params) => (
                <TextField {...params} label={t('bookings.select_employee')} fullWidth />
              )}
              onChange={(e, user) =>
                setNewBooking((prev) => ({ ...prev, userId: user?.id || '' }))
              }
            />
            <TextField
              fullWidth
              label={t('user.guest_name')}
              value={newBooking.guestName}
              onChange={(e) => setNewBooking((prev) => ({ ...prev, guestName: e.target.value }))}
            />
            <Autocomplete
                freeSolo
                options={companySuggestions}
                inputValue={newBooking.guestCompany}
                onInputChange={(event, newInputValue) => setNewBooking((prev) => ({ ...prev, guestCompany: newInputValue }))}
                renderInput={(params) => (
                    <TextField {...params} label={t('user.guest_company')} placeholder="Enter or select a company" />
                )}
                sx={{ width: '100%' }}
            />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              options={users}
              getOptionLabel={(user) => user.email || ''} 
              value={users.find((user) => user.id === newBooking.userId) || null} 
              renderInput={(params) => (
                <TextField {...params} label={t('bookings.select_employee')} fullWidth />
              )}
              onChange={(e, user) =>
                setNewBooking((prev) => ({ ...prev, userId: user?.id || '' }))
              }
            />
          </Box>
        )}

        <Box mt={2}>
          <Button
            fullWidth
            onClick={handleBookingSubmit}
            variant="contained"
            color="primary"
          >
            {t('actions.create_booking')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

BookingList.propTypes = {
  selectedDate: PropTypes.string,
  setSnackbar: PropTypes.func.isRequired,
  reloadCalendarData: PropTypes.func.isRequired,
};

export default BookingList;
